<template>
  <div>
    <BaseWizard
      :active-index="activeIndex"
      :tabs="tabs"
      @close-modal="closeModal"
      ref="baseWizard"
    >
      <ImportTrackers
        v-if="activeIndex === 0"
        data-test="import-trackers"
        @block-cancel-btn="blockCancelBtn"
        @enable-next-step="onEnableNextStep"
      />
      <TrackersSelection
        v-if="activeIndex === 1"
        data-test="tracker-selection"
        :headers="parsedFileHeaders"
        :items="fileResults"
        :reference-column="referenceColumn"
        :selected-trackers="trackerItemsForValidation"
        @disable-next="onDisableNext"
        @set-reference-column="updateReferenceColumn"
        @send-tracker-data="updateTrackerData"
      />
      <TrackersHealth
        v-if="isLastStep"
        data-test="tracker-health"
        :reference-column="referenceColumn"
        @disable-next="onDisableNext"
      />
      <BaseWizardFooter
        :next-text="'Next'"
        :show-back-button="true"
        :next-icon="true"
        :next-disabled="nextDisabled || isLoading"
        :last-disabled="lastDisabled"
        @block-cancel-btn="blockCancelBtn"
        @previous-step="onPreviousStep"
        @next-step="onNextStep"
      />
    </BaseWizard>
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
} from 'vuex';
import * as trackersTypes from '@/modules/trackers/store/types';
import ImportTrackers from '@/modules/trackers/components/ImportTrackers';
import TrackersSelection from '@/modules/trackers/components/TrackersSelection';
import TrackersHealth from '@/modules/trackers/components/TrackersHealth.vue';

export default {
  name: 'ImportWizard',
  components: {
    ImportTrackers,
    TrackersSelection,
    TrackersHealth,
  },

  data() {
    return {
      isLoading: false,
      activeIndex: 0,
      tabs: [{
        subtitle: 'Import trackers using spreadsheet',
      }, {
        subtitle: 'Assign tracker combinations',
      }, {
        subtitle: 'Tracker health check and content match',
      }],
      referenceColumn: 'Placement Name',
      trackerItemsForValidation: [],
      nextDisabled: true,
      lastDisabled: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'campaignFeedId',
    ]),
    ...mapState('content', [
      'creatives',
      'contentItems',
    ]),
    ...mapState('trackers', [
      'parsedFileHeaders',
      'parsedFileResult',
      'isImportingPlacementLevelTrackers',
      'selectedTrackersForValidation',
    ]),
    ...mapGetters('trackers', {
      combinations: trackersTypes.GET_ALL_COMBINATIONS,
    }),
    fileResults() {
      return this.parsedFileResult.map((item) => ({
        ...item,
        creativeIndex: null,
        publisherIndex: null,
        geoIndex: null,
      }));
    },

    isLastStep() {
      return this.activeIndex === 2;
    },

    totalTrackers() {
      return this.trackerItemsForValidation.length;
    },
  },

  methods: {
    ...mapActions('trackers', [
      trackersTypes.SAVE_TRACKERS,
      trackersTypes.RESET_IMPORT_TRACKERS,
      trackersTypes.SET_IMPORTING_PLACEMENT_LEVEL_TRACKERS,
    ]),
    convertTrackerItemsForPost(trackerItems) {
      const scope = this.isImportingPlacementLevelTrackers ? 'FEED' : 'CONTENT_ITEM';
      return trackerItems.filter((item) => item.selected).map((item) => {
        const contentItem = this.contentItems.find((cItem) => cItem.id === item.contentItemId);

        const getCombinationValue = (type, index) => this.combinations[type][index];
        const checkGeoType = (type, combinationItem) => (combinationItem?.type === type ? combinationItem : null);

        const templateId = getCombinationValue('creatives', item.creativeIndex)?.id;
        const publisherId = getCombinationValue('publishers', item.publisherIndex)?.id;
        const regionId = checkGeoType('region', getCombinationValue('geos', item.geoIndex))?.id;
        const countryId = checkGeoType('country', getCombinationValue('geos', item.geoIndex))?.id;

        const {
          uuid, impressionTracker, imageTrackers, scriptTrackers, clickTracker, postClickTracker,
        } = item;
        const encodedExternalId = contentItem ? contentItem.encodedExternalId : null;
        const { campaignFeedId } = this;

        let referenceId = null;
        if (this.isImportingPlacementLevelTrackers) referenceId = campaignFeedId;
        if (contentItem && !this.isImportingPlacementLevelTrackers) referenceId = encodedExternalId;
        const creativeId = this.creatives.find((creative) => creative.creativeTemplateId === templateId)?.id;

        const returnValue = {
          creativeId,
          publisherId,

          // these two rely on a combinations structure that doesn't exits yet.
          countryIds: [],
          geoGroupIds: [],

          uuid,
          scope,
          referenceId,

          clickTracker: clickTracker?.value ? clickTracker?.value : null,
          impressionTracker: impressionTracker?.value ? impressionTracker?.value : null,
          postClickTracker: postClickTracker?.value ? postClickTracker?.value : null,
          imageTrackers: imageTrackers.map((tracker) => tracker.value),
          scriptTrackers: scriptTrackers.map((tracker) => tracker.value),
        };

        if (regionId) returnValue.geoGroupIds = regionId ? [regionId] : [];
        if (countryId) returnValue.countryIds = countryId ? [countryId] : [];
        if (!countryId && !regionId) {
          returnValue.countryIds = [];
          returnValue.geoGroupIds = [];
        }

        return returnValue;
      });
    },

    onPreviousStep() {
      if (this.activeIndex === 0) {
        return this.$root.$emit('bv::hide::modal', 'trackersModal');
      }
      if (this.activeIndex === 1) {
        this.nextDisabled = true;
      }
      if (this.activeIndex === 2) {
        // TODO
      }
      this.activeIndex--;
    },

    goToOverview() {
      this.$emit('closed');
      this.$root.$emit('bv::hide::modal', 'trackersModal');
    },

    onEnableNextStep() {
      this.nextDisabled = false;
      this.blockCancelBtn(false);
    },

    blockCancelBtn(state) {
      this.lastDisabled = state;
      this.$refs.baseWizard.cancelBtnIsDisabled = state;
    },

    async onNextStep() {
      if (this.activeIndex === 2) {
        this.isLoading = true;
        const preparedTrackers = this.convertTrackerItemsForPost(this.selectedTrackersForValidation);
        try {
          await this.SAVE_TRACKERS(preparedTrackers);
          this.isLoading = false;
          this.goToOverview();
          this.RESET_IMPORT_TRACKERS();
          this.SET_IMPORTING_PLACEMENT_LEVEL_TRACKERS(false);
        } catch (e) {
          this.isLoading = false;
        }
        return;
      }
      this.activeIndex++;
    },

    onDisableNext(payload) {
      this.nextDisabled = payload;
    },
    updateTrackerData() {
    // updateTrackerData(referenceColumn, selectedTrackers) {
    //   this.SET_SELECTED_TRACKER_RESULTS({ referenceColumn, selectedTrackers });
    },

    updateReferenceColumn(val) {
      this.referenceColumn = val;
    },
    closeModal() {
      this.$root.$emit('bv::hide::modal', 'trackersModal');
    },
  },
  watch: {
    parsedFileHeaders(newVal) {
      if (!newVal.includes(this.referenceColumn)) this.referenceColumn = newVal[0];
    },
    isValidationFinished() {
      if (this.activeIndex === 2) {
        // this.SET_IS_WIZARD_NEXT_DISABLED(!this.isValidationFinished);
        // this.SET_IS_WIZARD_LAST_DISABLED(!this.isValidationFinished);
      }
    },
  },
  created() {
  },
};
</script>
